// Live-data preview for the 'single-classic' capture template.
//
// Renders one record at a time as a stacked header form + (read-only)
// detail grids — the visual layout the structural mock used — but
// backed by the same useEmbedRecords + livePreviewProcess pipeline as
// the list+form and tabular templates, so the record is real,
// validations run server-side, and selection variables bind to the
// active record for downstream embeds.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  AlertCircle, ChevronLeft, ChevronRight, FilePlus, Loader2, Lock,
  RefreshCw, Save, Trash2,
} from 'lucide-react'

import { embedLoadModel, embedRunAction, applyActionResultViaOnChange, makeEmbedUploader } from '@/livepreview/embedDataApi'
import { normalizeEmbedMode, embedModeCaps } from '@twasta/page-schema'
import type { DataAccess } from '@/pages/workbench/smartpage-preview/dataBinding'
import { useObjectVersion } from '@/store/objectVersionStore'
import {
  ChildTabularGrid, type ChildRow,
  useGridLayout, type GridLayoutApi,
} from '@twasta/page-runtime/txn/list'
import { buildParentPk, childFormsOf, headerFormNo, TxnRuntimeProvider } from '@twasta/page-runtime/txn'
import { designerEmbedDataApi } from '@/livepreview/captureRuntime'
import { makeDesignerTxnRuntime } from '@/livepreview/runtime/designerTxnRuntime'
import { saveTransactionObject } from '@/api/designers'
import { modelOverlayPatch } from '@/livepreview/api'

import { livePreviewDeploy } from '@/livepreview/api'
import {
  columnDbName, mainSqlModel, parseApiError,
} from '@/livepreview/utils'
import FormRenderer, { fieldAnchorId } from '@/livepreview/runtime/FormRenderer'
import ErrorPanel from './embedTable/ErrorPanel'
import type { GridErrorItem } from './embedTable/gridTypes'
import {
  useEmbedRecords, type EmbedRecord,
} from '@/livepreview/useEmbedRecords'
import { useWarningConfirm, FormModeBadge } from '@twasta/page-runtime/txn'
import { useItemChange } from '@/livepreview/useItemChange'
import type { RecordStatus } from '@/livepreview/types'

import type {
  ColumnDef, SqlModel, TransactionModel,
} from '@/pages/designers/transaction/types'
import type { EmbedConfig, PageInput } from './types'

interface Props {
  project: string
  embed: EmbedConfig
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  onSetInput?: (name: string, value: unknown) => void
  /** Fired after a successful batch save (modal presentation uses it to
   *  confirm and auto-close). */
  onSaved?: () => void
  /** Site-mode routing for model + CRUD (end-user published page). */
  dataAccess?: DataAccess
}

const deployedTxns = new Map<string, Promise<void>>()
function deployKey(project: string, obj: string) { return `${project}::${obj}` }

async function ensureDeployed(project: string, obj: string): Promise<void> {
  const key = deployKey(project, obj)
  const existing = deployedTxns.get(key)
  if (existing) return existing
  const p = livePreviewDeploy(project, obj).then(() => undefined)
  deployedTxns.set(key, p)
  try {
    await p
  } catch (err) {
    deployedTxns.delete(key)
    throw err
  }
}

const STATUS_LABEL: Record<RecordStatus, string> = {
  retrieved: 'retrieved', dirty: 'edited', unsaved: 'new',
  saved: 'saved', failed: 'failed', partial: 'partial',
}
const STATUS_BG: Record<RecordStatus, string> = {
  retrieved: 'rgba(100,116,139,0.18)', dirty: 'rgba(234,179,8,0.20)',
  unsaved: 'rgba(59,130,246,0.18)', saved: 'rgba(16,185,129,0.20)',
  failed: 'rgba(239,68,68,0.20)', partial: 'rgba(249,115,22,0.20)',
}
const STATUS_FG: Record<RecordStatus, string> = {
  retrieved: '#475569', dirty: '#a16207', unsaved: '#1d4ed8',
  saved: '#047857', failed: '#b91c1c', partial: '#c2410c',
}

function StatusBadge({ status }: { status: RecordStatus }) {
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 10,
      fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.04em',
      background: STATUS_BG[status], color: STATUS_FG[status],
    }}>{STATUS_LABEL[status]}</span>
  )
}

function SyncStatus({
  inSync, counts,
}: {
  inSync: boolean
  counts: Record<RecordStatus, number>
}) {
  if (inSync) {
    const total = counts.retrieved + counts.saved
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 10,
        fontSize: '0.8125rem', fontWeight: 600,
        background: 'rgba(16,185,129,0.18)', color: '#047857',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }} title="Local state matches the server">
        ● in sync{total > 0 ? ` · ${total}` : ''}
      </span>
    )
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 10,
      fontSize: '0.8125rem', fontWeight: 600,
      background: 'rgba(234,179,8,0.18)', color: '#a16207',
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }} title="Local edits have not been pushed to the server">
      ● not synced
    </span>
  )
}

export default function EmbedCaptureSingleClassicPreview({
  project, embed, inputs: _inputs, inputValues, onSetInput, onSaved, dataAccess,
}: Props) {
  const objName = embed.obj_name
  // The transport the shared form + lookup modal read from context. Declared
  // HERE, above the early returns below (`!objName`, loading, error, `!sm`) —
  // a hook after one of those changes the hook count between renders and
  // React throws #310, which is how the first attempt at this failed.
  const txnRuntime = useMemo(() => makeDesignerTxnRuntime(project), [project])
  const accessMode = normalizeEmbedMode(embed)
  // Canonical capability set for this access mode — the single source of truth
  // for which affordances render (mirrors EmbedListInterfaceHost). `single_add`
  // is `headerAdd:false`, so the "New" button below is correctly hidden.
  const caps = embedModeCaps(accessMode)
  // Add-only presentation (single_add, e.g. a modal "Add …" button): instead
  // of listing existing rows, open straight on one blank record pre-filled from
  // the embed's args. No retrieve — the user only ever creates here.
  const isAddOnly = accessMode === 'single_add'
  const txnVersion = useObjectVersion('T', project, objName || '')

  const [model, setModel] = useState<TransactionModel | null>(null)
  const [modelError, setModelError] = useState<string | null>(null)
  const [modelLoading, setModelLoading] = useState(false)

  useEffect(() => {
    if (!project || !objName) {
      setModel(null)
      setModelError(null)
      return
    }
    let cancelled = false
    setModelLoading(true)
    setModelError(null)
    embedLoadModel(dataAccess, project, objName)
      .then((res) => {
        if (cancelled) return
        const content = res?.content
        if (!content) {
          setModel(null)
          setModelError(`Transaction "${objName}" not yet generated`)
          return
        }
        const m = content.transaction || content
        setModel(m as TransactionModel)
      })
      .catch(() => {
        if (cancelled) return
        setModelError('Failed to load transaction')
      })
      .finally(() => { if (!cancelled) setModelLoading(false) })
    return () => { cancelled = true }
  }, [project, objName, txnVersion, dataAccess])

  const sm = useMemo(() => (model ? mainSqlModel(model) : null), [model])
  // The header's *direct* child forms (with their form_no), each rendered as an
  // editable detail grid. Deeper forms (a child's own children) nest inside
  // their parent row's sub-grid via ChildTabularGrid recursion, so only the
  // header's direct children appear as top-level detail grids here.
  const details = useMemo<Array<{ formNo: string; sm: SqlModel }>>(() => {
    if (!model) return []
    return childFormsOf(model, headerFormNo(model))
  }, [model])

  const [deployState, setDeployState] = useState<
    'idle' | 'deploying' | 'deployed' | 'error'
  >('idle')
  const [deployError, setDeployError] = useState<string | null>(null)

  // "Ignore warnings and proceed" confirm for business-logic warnings on save.
  const { confirmWarnings, warningsModal } = useWarningConfirm()
  const records = useEmbedRecords({
    project, objName: objName || '', inputValues,
    filters: embed.filters || [], args: embed.args || [],
    recordVar: embed.recordVar,
    // The FULL model, not just the header's sql_model: a `recordVar` draft
    // carrying detail rows is mapped onto the CHILD forms, which can only be
    // found through it. Without it the rows still arrived but each one
    // skipped its item_change cascade, so a line showed a product code with
    // no name, no price and no tax rate.
    sm, model, dataAccess,
    confirmWarnings,
  })

  // ── Editable detail (child) grids ──────────────────────────
  // Live column-layout override for the child grids (drag to resize/reorder,
  // and — for the author — apply back to the design). End-users on a published
  // site may rearrange their own view but never persist it (canEdit=false).
  const canEditDesign = (dataAccess?.mode ?? '') !== 'site'
  const layoutNotify = useMemo(() => ({
    success: (m: string) => { toast.success(m) },
    error: (m: string) => { toast.error(m) },
  }), [])
  const gridLayout: GridLayoutApi = useGridLayout({
    model, setModel, project, objName: objName || '', canEdit: canEditDesign,
    mode: 'design',
    notify: layoutNotify,
    persistence: {
      saveDesign: (proj, obj, m) => saveTransactionObject(proj, obj, { transaction: m }),
      saveOverlay: (proj, obj, set) => modelOverlayPatch(proj, obj, set, [], 'T'),
    },
  })
  const childUploader = useMemo(
    () => makeEmbedUploader(dataAccess, project),
    [dataAccess, project],
  )
  // Fetch existing child rows for one detail form scoped to a parent record —
  // used to lazy-load a saved parent's children when its sub-grid expands.
  const loadChildRows = useCallback(async (
    formNo: string, parentPk: Record<string, unknown>,
  ): Promise<Array<Record<string, unknown>>> => {
    const res = await designerEmbedDataApi.list(dataAccess, project, objName || '', 200, 0, formNo, parentPk)
    return (res.rows || []) as Array<Record<string, unknown>>
  }, [dataAccess, project, objName])
  // Everything the detail grids need, bundled and threaded into RecordBody.
  const childGrid = useMemo(() => ({
    execute: designerEmbedDataApi.execute,
    updateDetailRows: records.updateDetailRows,
    seedDetails: records.seedDetails,
    seedRowChildren: records.seedRowChildren,
    loadChildRows,
    uploadDocument: childUploader,
    dataAccess,
    busy: records.busy || deployState !== 'deployed',
    model,
  }), [records.updateDetailRows, records.seedDetails, records.seedRowChildren, loadChildRows, childUploader, dataAccess, records.busy, deployState, model])

  const filtersSig = useMemo(() => {
    const fs = embed.filters || []
    return JSON.stringify(fs.map((f) => {
      const ref = f.value
      let val: unknown
      if (ref?.source === 'page_input' && ref.name) val = inputValues[ref.name] ?? ''
      else if (ref) val = ref.value ?? ''
      let fromVal: unknown
      let toVal: unknown
      if (f.op === 'between') {
        if (f.from?.source === 'page_input' && f.from.name) fromVal = inputValues[f.from.name] ?? ''
        else if (f.from) fromVal = f.from.value ?? ''
        if (f.to?.source === 'page_input' && f.to.name) toVal = inputValues[f.to.name] ?? ''
        else if (f.to) toVal = f.to.value ?? ''
      }
      return { c: f.column, o: f.op, v: val, f: fromVal, t: toVal }
    }))
  }, [embed.filters, inputValues])

  const initialRetrieveDoneRef = useRef(false)
  useEffect(() => {
    if (!initialRetrieveDoneRef.current) {
      initialRetrieveDoneRef.current = true
      return
    }
    if (isAddOnly) return  // add-only never lists existing rows
    if (deployState !== 'deployed') return
    if (records.hasPendingChanges) return
    records.retrieve().catch(() => { /* surfaced via lastSaveError */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersSig, deployState])

  // Add-only: once deployed and the model is loaded, seed exactly one blank
  // record pre-filled from the embed's args (parent / company / nature here).
  const addSeededRef = useRef(false)
  useEffect(() => {
    if (!isAddOnly) return
    if (deployState !== 'deployed' || !model) return
    if (addSeededRef.current) return
    if (records.records.length > 0 || records.hasPendingChanges) return
    addSeededRef.current = true
    records.addNew().catch((err) => {
      addSeededRef.current = false
      const { message } = parseApiError(err)
      toast.error(`Could not open the add form: ${message}`)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddOnly, deployState, model])

  useEffect(() => {
    if (!project || !objName) return
    let cancelled = false
    // Site mode (public end-user page): the schema is already deployed by the
    // author, and site users must not call the author-only deploy endpoint
    // (it 401s). Treat as deployed and go straight to listing.
    if (dataAccess?.mode === 'site') {
      setDeployState('deployed')
      if (!isAddOnly) void records.retrieve().catch(() => { /* non-fatal */ })
      return () => { cancelled = true }
    }
    setDeployState('deploying')
    setDeployError(null)
    ensureDeployed(project, objName)
      .then(async () => {
        if (cancelled) return
        setDeployState('deployed')
        if (!isAddOnly) {
          try { await records.retrieve() } catch { /* non-fatal */ }
        }
      })
      .catch((err) => {
        if (cancelled) return
        const { message } = parseApiError(err)
        setDeployError(message)
        setDeployState('error')
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, dataAccess])

  // Single-classic shows one record at a time. ``activeIdx`` indexes
  // into records.records — defaults to the last record so a freshly
  // added or retrieved row is what the user sees first.
  const [activeIdx, setActiveIdx] = useState(0)
  // One-shot restoration guard — see EmbedCaptureTabPreview for the
  // same pattern. Only the first records-load arrival tries to honour
  // the saved selectedIdVar; subsequent navigation is user-driven.
  const restoredRef = useRef(false)
  useEffect(() => {
    // Keep activeIdx within bounds whenever the record list resizes.
    if (records.records.length === 0) {
      setActiveIdx(0)
      restoredRef.current = false
      return
    }
    if (!restoredRef.current) {
      restoredRef.current = true
      const varName = embed.selectedIdVar
      if (varName) {
        const raw = inputValues[varName]
        const targetPk = raw == null || raw === '' ? null : String(raw)
        if (targetPk) {
          const idx = records.records.findIndex((r) => r.primaryKey === targetPk)
          if (idx >= 0) {
            setActiveIdx(idx)
            lastSelectionRef.current = { id: targetPk, data: records.records[idx] }
            return
          }
        }
      }
    }
    if (activeIdx >= records.records.length) {
      setActiveIdx(records.records.length - 1)
    }
  }, [records.records, activeIdx, embed.selectedIdVar, inputValues])

  const activeRec: EmbedRecord | null =
    records.records[activeIdx] ?? null

  // Selection plumbing.
  const lastSelectionRef = useRef<{ id: string | null; data: unknown }>({
    id: null, data: null,
  })
  const writeSelectionVars = useCallback((rec: EmbedRecord | null) => {
    if (!onSetInput) return
    const pk = rec?.primaryKey ?? null
    const last = lastSelectionRef.current
    if (embed.selectedIdVar && last.id !== pk) {
      onSetInput(embed.selectedIdVar, pk ?? '')
    }
    if (embed.selectedDataVar && last.data !== rec) {
      onSetInput(embed.selectedDataVar, rec ? rec.header : undefined)
    }
    lastSelectionRef.current = { id: pk, data: rec }
  }, [embed.selectedIdVar, embed.selectedDataVar, onSetInput])

  useEffect(() => {
    writeSelectionVars(activeRec)
  }, [activeRec, writeSelectionVars])

  // Edit mode: a retrieved header record arrives with an EMPTY details buffer
  // (the list endpoint returns header rows only), so the detail grids — which
  // render `rec.details[formNo]` — would show nothing for an existing record.
  // Lazily pull each detail form's rows for the active persisted record and
  // seed them so the grids show what's on file. Guarded by `details[formNo]
  // !== undefined` (seedDetails marks it defined and never clobbers in-progress
  // edits) plus an in-flight set so a record with no rows isn't refetched every
  // render. New (unsaved) records have no primaryKey, so they skip this.
  const childLoadInFlight = useRef<Set<string>>(new Set())
  useEffect(() => {
    const rec = activeRec
    if (!rec || !rec.primaryKey || !sm || details.length === 0) return
    if (deployState !== 'deployed') return
    const parentPk = buildParentPk(sm, rec.header)
    for (const d of details) {
      if (rec.details[d.formNo] !== undefined) continue
      const key = `${rec.id} ${d.formNo}`
      if (childLoadInFlight.current.has(key)) continue
      childLoadInFlight.current.add(key)
      loadChildRows(d.formNo, parentPk)
        .then((rows) => records.seedDetails(rec.id, d.formNo, rows))
        .catch(() => records.seedDetails(rec.id, d.formNo, []))
        .finally(() => childLoadInFlight.current.delete(key))
    }
  }, [activeRec, sm, details, deployState, loadChildRows, records])

  // Item-change cascade.
  const triggerItemChange = useItemChange<{ recordId: string }>({
    project, objName: objName || '', sm, dataAccess,
    onUpdates: (ctx, updates) => {
      records.updateHeader(ctx.recordId, updates)
    },
  })

  const onFieldChange = useCallback((name: string, v: unknown) => {
    if (!activeRec) return
    const newHeader = { ...activeRec.header, [name]: v }
    records.updateHeader(activeRec.id, { [name]: v })
    triggerItemChange({ recordId: activeRec.id }, name, newHeader)
    writeSelectionVars({ ...activeRec, header: newHeader })
  }, [activeRec, records, triggerItemChange, writeSelectionVars])

  // Actions.
  const handleAdd = useCallback(async () => {
    try {
      const rec = await records.addNew()
      // Make the new record active so the user lands on a blank form.
      // The state update below races setRecords, so we delay via a
      // micro-task to land after React commits the new record.
      queueMicrotask(() => {
        setActiveIdx((prev) => {
          // Best guess: the appended record is at the end of the new
          // array. records.records inside this closure is stale though,
          // so we move to "last index" relative to current state at
          // render time (clamped by the bounds-effect).
          return prev + 1
        })
        writeSelectionVars(rec)
      })
    } catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Could not add new record: ${message}`)
    }
  }, [records, writeSelectionVars])

  const handleSaveAll = useCallback(async () => {
    const outcome = await records.saveBatch()
    if (outcome.success) {
      toast.success('All records saved')
      onSaved?.()
    } else if (outcome.failedCount > 0) {
      toast.error(`${outcome.failedCount} record${outcome.failedCount === 1 ? '' : 's'} failed validation`)
    }
  }, [records, onSaved])

  const handleRetrieve = useCallback(async () => {
    if (records.hasPendingChanges) return
    try { await records.retrieve() }
    catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Refresh failed: ${message}`)
    }
  }, [records])

  // An action button's business logic may have rewritten the record it ran on —
  // most consequentially its STATUS, which is what an edit-lock rule reads. Pull
  // the record back so the form reflects the action immediately (a submit that
  // closes the form locks it here, with no manual refresh). Runs even with local
  // edits pending — a retrieve merges the user's per-column deltas back on top
  // of the fresh row, so nothing typed is lost, and a record the action just
  // locked must stop offering Save the moment it is locked, not on the next
  // refresh. Add-only embeds never list, so there is nothing to re-read.
  const handleActionDone = useCallback(() => {
    if (isAddOnly) return
    records.retrieve().catch(() => { /* the toast from the action is enough */ })
  }, [isAddOnly, records])

  const handleDiscardChanges = useCallback(async () => {
    const pending = records.counts.unsaved + records.counts.dirty +
      records.counts.failed + records.counts.partial
    if (!window.confirm(
      `Discard ${pending} pending change${pending === 1 ? '' : 's'} and reload from the server?`,
    )) return
    records.discardLocalEdits()
    try { await records.retrieve() }
    catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Refresh failed: ${message}`)
    }
  }, [records])

  const handlePrev = useCallback(() => {
    setActiveIdx((i) => Math.max(0, i - 1))
  }, [])
  const handleNext = useCallback(() => {
    setActiveIdx((i) => Math.min(records.records.length - 1, i + 1))
  }, [records.records.length])

  // ── Error side panel (aggregates across all records — single-classic
  // can carry multiple records when the user pages through with prev/next).
  // NOTE: these hooks MUST stay ABOVE the early returns below. `sm`
  // resolves from null → defined as the transaction model loads, so a
  // conditional hook here would change the hook count between renders
  // (React error #310). columnLabelFromSm is null-safe for `sm`. ──
  const total = records.records.length
  const errorItems: GridErrorItem[] = useMemo(() => {
    const items: GridErrorItem[] = []
    records.records.forEach((rec, idx) => {
      const rowLabel = rec.primaryKey
        ? `Record — ${String(rec.primaryKey)}`
        : `Record — New ${idx + 1}`
      if (rec.recordError) {
        items.push({
          rowKey: rec.id, rowLabel,
          column: null, columnLabel: null,
          message: rec.recordError,
          section: total > 1 ? `Record ${idx + 1} of ${total}` : undefined,
        })
      }
      for (const [field, msg] of Object.entries(rec.fieldErrors || {})) {
        items.push({
          rowKey: rec.id, rowLabel,
          column: field,
          columnLabel: columnLabelFromSm(sm, field),
          message: msg,
          section: total > 1 ? `Record ${idx + 1} of ${total}` : undefined,
        })
      }
    })
    return items
  }, [records.records, sm, total])

  const [errorPanelOpen, setErrorPanelOpen] = useState(false)
  const errorCount = errorItems.length
  useEffect(() => {
    if (errorCount > 0 && records.lastSaveError) setErrorPanelOpen(true)
    if (errorCount === 0) setErrorPanelOpen(false)
  }, [errorCount, records.lastSaveError])

  const handleErrorSelect = useCallback((item: GridErrorItem) => {
    const idx = records.records.findIndex(r => r.id === item.rowKey)
    if (idx >= 0) setActiveIdx(idx)
    if (item.column) {
      requestAnimationFrame(() => {
        const el = document.getElementById(fieldAnchorId(item.column!))
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        const input = el.querySelector<HTMLElement>(
          'input, select, textarea, button',
        )
        if (input) input.focus()
      })
    }
  }, [records.records])

  // ── Render ───────────────────────────────────────────────

  if (!objName) {
    return (
      <div style={emptyStyle}>
        <AlertCircle size={18} />
        <span>Pick a transaction in the property panel to embed.</span>
      </div>
    )
  }
  if (modelLoading) {
    return (
      <div style={emptyStyle}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading transaction model…</span>
      </div>
    )
  }
  if (modelError) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>{modelError}</span>
      </div>
    )
  }
  if (!sm) return null

  const pending = records.counts.unsaved + records.counts.dirty +
    records.counts.failed + records.counts.partial
  // The engine marks a record view-only when the designer's edit-lock rule
  // matches it (or it awaits approval). The whole record goes read-only: fields
  // disabled, the custom action bar (Submit …) gone, Save off — and the engine
  // refuses the write regardless, so this is presentation, not the guard.
  const locked = !!activeRec && activeRec.editable === false
  const lockReason = (activeRec?.lockReason || '').trim()
    || 'This record is locked and cannot be edited.'

  return (
    // The shared form and its lookup modal read their transport from the
    // TxnRuntime context, exactly as the other two capture templates do
    // (EmbedCapturePreview / EmbedCaptureTabularPreview both provide it).
    // This one never did, and nothing noticed while its DETAIL grids were
    // always empty — the moment a record arrived carrying child rows, the
    // grid rendered a row editor and threw "useTxnRuntime must be used
    // within a <TxnRuntimeProvider>", taking the whole block down.
    <TxnRuntimeProvider value={txnRuntime}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <SyncStatus inSync={!records.hasPendingChanges} counts={records.counts} />
          {deployState === 'deploying' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
              deploying…
            </span>
          )}
          {deployState === 'error' && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-error, #b91c1c)' }}>
              deploy failed: {deployError}
            </span>
          )}
          {total > 1 && (
            <>
              <button
                type="button" className="btn btn-ghost btn-sm"
                onClick={handlePrev}
                disabled={records.busy || activeIdx <= 0}
                title="Previous record"
              ><ChevronLeft size={14} /></button>
              <span style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)' }}>
                Record {activeIdx + 1} of {total}
              </span>
              <button
                type="button" className="btn btn-ghost btn-sm"
                onClick={handleNext}
                disabled={records.busy || activeIdx >= total - 1}
                title="Next record"
              ><ChevronRight size={14} /></button>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {errorCount > 0 && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setErrorPanelOpen(true)}
              style={{ color: '#b91c1c' }}
              title="Show all validation errors"
            >
              <AlertCircle size={14} /> {errorCount} error{errorCount === 1 ? '' : 's'}
            </button>
          )}
          {!isAddOnly && (
            <button
              type="button" className="btn btn-ghost btn-sm"
              onClick={handleRetrieve}
              disabled={records.busy || deployState !== 'deployed' || records.hasPendingChanges}
              title={
                records.hasPendingChanges
                  ? 'Local changes are not synced — Save or Discard them before refreshing'
                  : 'Reload from the server'
              }
            ><RefreshCw size={14} /> Refresh</button>
          )}
          {records.hasPendingChanges && (
            <button
              type="button" className="btn btn-ghost btn-sm"
              onClick={handleDiscardChanges}
              disabled={records.busy || deployState !== 'deployed'}
              title="Drop pending changes and reload"
              style={{ color: '#b91c1c' }}
            ><Trash2 size={14} /> Discard changes</button>
          )}
          {caps.headerAdd && (
            <button
              type="button" className="btn btn-ghost btn-sm"
              onClick={handleAdd}
              disabled={records.busy || deployState !== 'deployed'}
            ><FilePlus size={14} /> New</button>
          )}
          <button
            type="button" className="btn btn-primary btn-sm"
            onClick={handleSaveAll}
            disabled={records.busy || deployState !== 'deployed' || pending === 0 || locked}
            title={locked ? lockReason : undefined}
          >
            <Save size={14} /> {embed.captureSaveLabel || 'Save'}
            {pending > 0 && (
              <span style={{ marginLeft: 4, opacity: 0.85 }}>({pending})</span>
            )}
          </button>
        </div>
      </div>

      {records.hasPendingChanges && (
        <div style={{
          padding: '6px 10px', fontSize: '0.8425rem',
          background: 'rgba(234,179,8,0.10)',
          border: '1px solid rgba(234,179,8,0.40)',
          borderRadius: 6, color: '#a16207',
        }}>
          Local changes are not synced. Refresh is disabled until you{' '}
          <strong>{embed.captureSaveLabel || 'Save'}</strong> or{' '}
          <strong>Discard changes</strong>.
        </div>
      )}

      {locked && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 10px', fontSize: '0.8425rem',
          background: 'rgba(180,83,9,0.10)',
          border: '1px solid rgba(180,83,9,0.40)',
          borderRadius: 6, color: '#b45309',
        }}>
          <Lock size={14} /> {lockReason}
        </div>
      )}

      {records.lastSaveError && (
        <div style={{
          padding: '6px 10px',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.35)',
          borderRadius: 6, color: '#b91c1c', fontSize: '0.8425rem',
        }}>{records.lastSaveError}</div>
      )}

      {/* Form area */}
      {activeRec ? (
        <RecordBody
          rec={activeRec}
          sm={sm}
          details={details}
          project={project}
          objName={objName}
          columnLabel={(dbName) => columnLabelFromSm(sm, dbName)}
          onFieldChange={onFieldChange}
          uploadDocument={childUploader}
          dataAccess={dataAccess}
          childGrid={childGrid}
          gridLayout={gridLayout}
          viewOnly={locked}
          lockReason={locked ? lockReason : undefined}
          onActionDone={handleActionDone}
        />
      ) : (
        <div style={{
          padding: '24px 16px', textAlign: 'center',
          color: 'var(--color-text-secondary)', fontSize: '0.9125rem',
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)', borderRadius: 8,
        }}>
          No record yet. Click <strong>New</strong> to start a fresh
          form, or <strong>Refresh</strong> to load existing records
          from the server.
        </div>
      )}

      <ErrorPanel
        open={errorPanelOpen}
        errors={errorItems}
        onClose={() => setErrorPanelOpen(false)}
        onSelect={handleErrorSelect}
      />
      {/* "Ignore warnings and proceed" confirm for save warnings. */}
      {warningsModal}
    </div>
    </TxnRuntimeProvider>
  )
}

type ChildGridBundle = {
  execute: (
    da: DataAccess | undefined, project: string, objName: string,
    action: string, payload?: Record<string, unknown>,
  ) => Promise<unknown>
  updateDetailRows: (
    id: string, table: string,
    updater: (rows: Array<Record<string, unknown>>) => Array<Record<string, unknown>>,
    coalesceKey?: string,
  ) => void
  seedDetails: (id: string, table: string, rows: Array<Record<string, unknown>>) => void
  seedRowChildren: (
    id: string, childFormNo: string, childTxnId: string,
    gcFormNo: string, rows: Array<Record<string, unknown>>,
  ) => void
  loadChildRows: (formNo: string, parentPk: Record<string, unknown>) =>
    Promise<Array<Record<string, unknown>>>
  uploadDocument: (file: File) => Promise<{ url: string; name?: string }>
  dataAccess?: DataAccess
  busy: boolean
  model: TransactionModel | null
}

function RecordBody({
  rec, sm, details, project, objName, columnLabel, onFieldChange, uploadDocument, dataAccess,
  childGrid, gridLayout, viewOnly = false, lockReason, onActionDone,
}: {
  rec: EmbedRecord
  sm: SqlModel
  details: Array<{ formNo: string; sm: SqlModel }>
  project: string
  objName: string
  columnLabel: (dbName: string) => string
  onFieldChange: (name: string, v: unknown) => void
  uploadDocument: (file: File) => Promise<{ url: string; name?: string }>
  dataAccess?: DataAccess
  childGrid: ChildGridBundle
  gridLayout: GridLayoutApi
  /** Record locked by the designer's edit-lock rule — read-only everywhere. */
  viewOnly?: boolean
  lockReason?: string
  onActionDone?: () => void
}) {
  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 8,
      background: 'var(--color-bg)', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <strong style={{ fontSize: '0.9125rem' }}>
            {sm.form_title || objName}
          </strong>
          <StatusBadge status={rec.status} />
          {viewOnly && <FormModeBadge mode="view" lockReason={lockReason} />}
          {rec.primaryKey && (
            <code style={{
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
              background: 'var(--color-bg)',
              padding: '2px 6px', borderRadius: 3,
            }}>
              {rec.primaryKey.slice(0, 18)}{rec.primaryKey.length > 18 ? '…' : ''}
            </code>
          )}
        </div>
      </div>
      {(rec.recordError || Object.keys(rec.fieldErrors).length > 0) && (
        <ValidationBanner
          fieldErrors={rec.fieldErrors}
          banner={rec.recordError}
          columnLabel={columnLabel}
        />
      )}
      <div style={{ padding: 14 }}>
        <FormRenderer
          model={sm}
          values={rec.header}
          errors={rec.fieldErrors}
          onChange={onFieldChange}
          readOnlyKey={!!rec.primaryKey}
          viewOnly={viewOnly}
          lockReason={lockReason}
          project={project}
          objName={objName}
          uploadDocument={uploadDocument}
          runAction={(p, o, req) => embedRunAction(dataAccess, p, o, req)}
          formNo={sm.form_no}
          txnId={rec.primaryKey || undefined}
          onActionResult={(res) => applyActionResultViaOnChange(res, sm.form_no || '1', onFieldChange)}
          onActionError={(err) => toast.error(parseApiError(err).message)}
          onActionDone={onActionDone}
        />
      </div>
      {details.length > 0 && (
        <div style={{
          padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
          borderTop: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)',
        }}>
          {details.map((d, di) => (
            <div key={d.formNo || di} style={{
              border: '1px solid var(--color-border)', borderRadius: 6,
              background: 'var(--color-bg)', overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '4px 10px', fontSize: '0.8125rem',
                background: 'var(--color-bg-secondary)',
                borderBottom: '1px solid var(--color-border)',
              }}>
                <span style={{ fontWeight: 600 }}>
                  {d.sm.form_title || `Detail ${di + 1}`}
                </span>
                <span style={{
                  padding: '1px 6px', borderRadius: 8, fontSize: '0.8125rem',
                  background: 'var(--color-accent-weak)', color: 'var(--color-accent)',
                }}>Detail grid</span>
              </div>
              {/* Editable multi-tabular child grid — same cell editors, Excel
                  paste/fill and Add row/Delete row as the tabular template.
                  Edits write into this record's details[formNo]; Save All (or
                  the single-add save) persists them with the header. A DEFINITE
                  height + overflow:hidden keeps the "Add row" toolbar pinned
                  while only the table body scrolls. */}
              <div style={{ height: 320, overflow: 'hidden' }}>
                <ChildTabularGrid
                  execute={childGrid.execute}
                  project={project}
                  objName={objName}
                  formNo={d.formNo}
                  childSm={d.sm}
                  rows={(rec.details[d.formNo] as ChildRow[]) || []}
                  onMutate={(updater, coalesceKey) =>
                    childGrid.updateDetailRows(rec.id, d.formNo, updater, coalesceKey)}
                  parentHeaderValues={rec.header}
                  uploadDocument={childGrid.uploadDocument}
                  dataAccess={childGrid.dataAccess}
                  busy={childGrid.busy}
                  parentStatus={rec.status}
                  gridLayout={gridLayout}
                  model={childGrid.model}
                  recordId={rec.id}
                  loadChildRows={childGrid.loadChildRows}
                  seedRowChildren={childGrid.seedRowChildren}
                  depth={1}
                  readOnly={viewOnly}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ValidationBanner({
  fieldErrors, banner, columnLabel,
}: {
  fieldErrors: Record<string, string>
  banner: string | null
  columnLabel: (dbName: string) => string
}) {
  const keys = Object.keys(fieldErrors)
  if (keys.length === 0 && !banner) return null
  return (
    <div style={{
      padding: '10px 16px',
      background: 'rgba(239,68,68,0.08)',
      borderBottom: '1px solid rgba(239,68,68,0.35)',
      color: '#ef4444', fontSize: '0.8425rem',
    }}>
      <div style={{ fontWeight: 600, marginBottom: keys.length ? 4 : 0 }}>
        {keys.length > 0
          ? `Save blocked — ${keys.length} validation error${keys.length === 1 ? '' : 's'}`
          : banner}
      </div>
      {keys.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {keys.map((k) => (
            <li key={k}>
              <strong>{columnLabel(k)}</strong>: {fieldErrors[k]}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function columnLabelFromSm(sm: SqlModel | null, dbName: string): string {
  if (!sm?.columns) return dbName
  for (const wrap of sm.columns) {
    const col = wrap.column as ColumnDef | undefined
    if (!col) continue
    const dbn = columnDbName(col)
    if (dbn === dbName) return col.heading || col.name || dbName
  }
  return dbName
}

const emptyStyle: React.CSSProperties = {
  padding: '20px 14px',
  display: 'flex', alignItems: 'center', gap: 10,
  background: 'var(--color-bg)',
  border: '1px dashed var(--color-border)', borderRadius: 8,
  color: 'var(--color-text-secondary)', fontSize: '0.9125rem',
}
